import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useContent } from "@/content/ContentContext";

const ease = [0.22, 1, 0.36, 1];

export default function Spotlight() {
  const content = useContent();
  const s = content.spotlight;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);

  return (
    <section
      ref={ref}
      data-testid="spotlight-section"
      className="relative overflow-hidden border-t border-line"
    >
      <motion.div
        style={{ y }}
        className="absolute inset-x-0 -inset-y-[12%]"
        aria-hidden="true"
      >
        <img
          src={s.image}
          alt=""
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-[#0B1A33]/88" />
      </motion.div>

      <div className="relative z-10 mx-auto max-w-[1440px] px-6 py-28 lg:px-12 lg:py-40">
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease }}
          className="text-xs uppercase tracking-[0.3em] text-harvestHover"
        >
          {s.overline}
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.1, ease }}
          data-testid="spotlight-heading"
          className="mt-6 max-w-3xl font-display text-4xl font-medium leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl"
        >
          {s.headingA}{" "}
          <span className="italic text-harvestHover">{s.headingAccent}</span>
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.15, ease }}
          className="mt-6 max-w-xl text-sm leading-relaxed text-white/75 sm:text-base"
        >
          {s.sub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.2, ease }}
          className="mt-14 flex flex-wrap gap-14"
        >
          {s.stats.map((st, i) => (
            <div key={st.label} data-testid={`spotlight-stat-${i}`}>
              <p className="font-display text-6xl font-medium tracking-tight text-harvestHover lg:text-7xl">
                {st.value}
              </p>
              <p className="mt-3 text-xs uppercase tracking-[0.2em] text-white/60">
                {st.label}
              </p>
            </div>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.25, ease }}
        >
          <Link
            to="/contact"
            data-testid="spotlight-enquire-button"
            className="group mt-14 inline-flex items-center gap-3 bg-harvest px-8 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-harvestHover"
          >
            {s.cta}
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
